import { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Breadcrumbs,
  Link,
} from "@mui/material";
import { Swiper, SwiperSlide } from "swiper/react";
import { Mousewheel, Keyboard } from "swiper/modules";
import "swiper/css";
import HomeIcon from "@mui/icons-material/Home";
import LoadingScreen from "../components/LoadingScreen";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import api from "../api/axios";
import { AuthContext } from "../context/AuthContext";
import { useWishlist } from "../context/WishlistContext";
import { useCart } from "../context/CartContext";
import ProductCard from "../components/ProductCard";
import MobileReelCard from "../components/MobileReelCard";

const colors = {
  primary: "#0056c3",
  surface: "#ffffff",
  outlineVariant: "#c2c6d6",
  textMuted: "#878787",
};

const BrowseHistory = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { toggleWishlist, isWishlisted } = useWishlist();
  const { addToCart } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [audioEnabled, setAudioEnabled] = useState(false);

  useEffect(() => {
    const fetchProducts = async () => {
      if (user) {
        // Load from API for logged-in users — no localStorage fallback
        try {
          const res = await api.get("/browse-history");
          if (res.data.success) {
            setProducts(res.data.products || []);
            setLoading(false);
            return;
          }
        } catch (err) {
          console.error("Error fetching browse history:", err);
        }
        setProducts([]);
        setLoading(false);
        return;
      }

      // Not logged in — load from localStorage
      const viewed = JSON.parse(localStorage.getItem("recentlyViewed")) || [];
      const limitedIds = viewed.slice(0, 10);
      if (!limitedIds.length) {
        setLoading(false);
        return;
      }
      try {
        const res = await api.post("/products/recently-viewed", { ids: limitedIds });
        setProducts(res.data.products || []);
      } catch (err) {
        console.error("Error fetching browse history:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [user]);

  const clearHistory = async () => {
    if (user) {
      try {
        await api.delete("/browse-history");
      } catch (err) {
        console.error("Failed to clear history:", err);
      }
    }
    localStorage.removeItem("recentlyViewed");
    setProducts([]);
  };

  const removeProduct = async (productId) => {
    if (user) {
      try {
        await api.delete(`/browse-history/item/${productId}`);
      } catch (err) {
        console.error("Failed to remove item:", err);
      }
    } else {
      // Not logged in — remove from localStorage
      const viewed = JSON.parse(localStorage.getItem("recentlyViewed")) || [];
      const updated = viewed.filter((id) => id !== productId);
      localStorage.setItem("recentlyViewed", JSON.stringify(updated));
    }
    setProducts(products.filter((p) => p._id !== productId));
  };

  if (loading) {
    return (
      <Box sx={{ py: 4, px: { xs: 2, sm: 4 }, minHeight: "100vh", background: "#f5f5f5" }}>
        <LoadingScreen message="Loading your history..." />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, background: "#f5f5f5", minHeight: "100vh" }}>
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
          <Link underline="hover" color="inherit" href="/customer/dashboard"
            onClick={(e) => { e.preventDefault(); navigate("/customer/dashboard"); }}
            sx={{ display: 'flex', alignItems: 'center' }}>
            <HomeIcon sx={{ mr: 0.5, fontSize: 20 }} /> Customer Dashboard
          </Link>
          <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
            Browsing History
          </Typography>
        </Breadcrumbs>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, flex: 1 }}>Browsing History</Typography>
        {products.length > 0 && (
          <Button variant="outlined" size="small" startIcon={<DeleteOutlineIcon />} onClick={clearHistory} sx={{ textTransform: "none" }}>
            Clear History
          </Button>
        )}
      </Box>

      {products.length === 0 ? (
        <Box sx={{ p: 4, textAlign: "center", borderRadius: 1, border: "1px solid", borderColor: colors.outlineVariant, bgcolor: colors.surface }}>
          <Box sx={{ fontSize: 60, mb: 2, opacity: 0.3 }}>📖</Box>
          <Typography sx={{ color: colors.textMuted, mb: 1 }}>No browsing history</Typography>
          <Typography sx={{ color: colors.textMuted, fontSize: 13, mb: 2 }}>Products you view will appear here</Typography>
          <Button variant="contained" onClick={() => navigate("/")}
            sx={{ bgcolor: "#FFD814", color: "#000", textTransform: "none", borderRadius: "100px", px: 4, "&:hover": { bgcolor: "#e6c512" } }}>
            Browse Reels
          </Button>
        </Box>
      ) : (
        <>
          {/* MOBILE: full-screen vertical swiper */}
          <Box sx={{ display: { xs: "block", sm: "none" }, position: "fixed", top: 0, left: 0, width: "100%", height: "100dvh", zIndex: 1200, bgcolor: "#000" }}>
            <Swiper direction="vertical" modules={[Mousewheel, Keyboard]} mousewheel={{ forceToAxis: true }} keyboard={{ enabled: true }}
              onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
              style={{ width: "100%", height: "100%", overflow: "hidden" }}>
              {products.map((product, index) => (
                <SwiperSlide key={product._id} style={{ height: "100%", margin: 0, padding: 0, overflow: "hidden" }}>
                  <MobileReelCard item={product} isActive={index === activeIndex}
                    onNavigate={navigate}
                    audioEnabled={audioEnabled}
                    onToggleAudio={() => setAudioEnabled(!audioEnabled)}
                    onAddToCart={(p) => addToCart({ id: p._id, title: p.title, price: Number(String(p.price).replace(/,/g, "")) || 0, imageUrl: p.imageUrl || p.images?.[0]?.thumbnailUrl || "", qty: 1 })}
                    onToggle={(p) => toggleWishlist({ id: p._id, title: p.title, price: p.price, imageUrl: p.imageUrl || p.images?.[0]?.thumbnailUrl, videoUrl: p.videoUrl, images: p.images })}
                    onBuyProduct={(id) => navigate(`/delivery-location/${id}`)} />
                </SwiperSlide>
              ))}
            </Swiper>
          </Box>

          {/* DESKTOP: grid layout */}
          <Box sx={{ display: { xs: "none", sm: "grid" }, gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 2 }}>
            {products.map((product) => (
              <ProductCard key={product._id} product={product} variant="browse-history"
                onNavigate={(id) => navigate(`/product/${id}`)}
                onBuyProduct={(id) => navigate(`/delivery-location/${id}`)}
                onRemove={(id) => removeProduct(id)}
                onAddToCart={(p) => addToCart({ id: p._id, title: p.title, price: Number(String(p.price).replace(/,/g, "")) || 0, imageUrl: p.imageUrl || p.images?.[0]?.thumbnailUrl || "", qty: 1 })}
                onToggleFavorite={(p) => toggleWishlist({ id: p._id, title: p.title, price: p.price, imageUrl: p.imageUrl || p.images?.[0]?.thumbnailUrl, videoUrl: p.videoUrl, images: p.images })}
                isWishlisted={isWishlisted(product._id)} />
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

export default BrowseHistory;